import { useCallback, useRef, useState } from 'react';
import type { VaultSaveSourceDto } from '@/lib/api';
import { clearSession, type PersistedSession } from '../lib/session-store';

/**
 * One open file in the editor.
 *
 * Active tab: `snapshot` is null — the live state lives in the editor's
 * useImageEditorState / Fabric canvas / useCanvasHistory hooks.
 *
 * Inactive tabs: `snapshot` is a serialized {@link PersistedSession} captured
 * at the moment the user switched away. Switching back restores it.
 */
export interface TabDoc {
  id: string;
  /** Stable per-tab UUID. Doubles as the IndexedDB key for this tab's
   *  session record AND for any associated PSD-source-stash entry.
   *  Survives reload (it's persisted in the snapshot); the React `id`
   *  field above regenerates on each session and is React-only state. */
  documentId: string;
  /** Display name (e.g. "photo.jpg"). Mirrors the editor's `state.fileName`. */
  fileName: string;
  /** Cloud file id, if the doc came from / is saved to Fias storage. */
  fileId: string | null;
  /**
   * File System Access API handle, present when the user opened a local
   * file in a browser that supports `window.showOpenFilePicker`. Save can
   * use this to overwrite the original file in place; without it (Firefox
   * / Safari, or files chosen via the `<input type="file">` fallback),
   * Save degrades to a download.
   */
  fileHandle: FileSystemFileHandle | null;
  /**
   * Original filename with its extension (e.g. "photo.jpg"). Stored
   * verbatim from the opened file so the Export dialog's format
   * dropdown can default to the source format when the user clicks
   * Save / Save-As. `fileName` strips the extension for display, and
   * `fileHandle.name` is null on non-FSA browsers — `sourceFileName`
   * is the only field that always carries the extension.
   */
  sourceFileName: string | null;
  /**
   * The Vault document whose tier a Save As keeps (ADR-039 amendment 2): the
   * one the tab was opened from — through the Fias picker or the Vault's
   * "Open in Image Editor" — and then the one it was last saved as. A Save As
   * sends it; the server reads it as the person. Null for local files.
   */
  derivedFrom: VaultSaveSourceDto | null;
  /** Whether the doc has unsaved edits. Drives the "*" indicator on the tab. */
  isDirty: boolean;
  /** Serialized state for inactive tabs; null while the tab is active. */
  snapshot: PersistedSession | null;
}

let tabIdSeq = 0;
function nextTabId(): string {
  tabIdSeq += 1;
  return `tab_${Date.now()}_${tabIdSeq}`;
}

interface UseDocumentTabsResult {
  tabs: TabDoc[];
  activeTabId: string | null;
  /**
   * Append a new tab and make it active. By default the newly active tab
   * starts with a null snapshot — caller is expected to populate the editor
   * with whatever content this tab represents (e.g. by calling loadImage).
   *
   * Pass `snapshot` to seed an inactive-style snapshot up-front; the
   * tab-switch effect will then apply it on the next render. Used by the
   * IndexedDB session-restore flow on mount to avoid racing the effect's
   * "freshly-created tab → resetEditorToEmpty" branch.
   *
   * Pass `documentId` to reuse an existing UUID — used by the restore flow
   * so the tab's IDB key matches what was persisted. Omit to mint a fresh
   * UUID for a newly-opened file. Also pass `activate: false` when seeding
   * multiple tabs during multi-tab restore (only one becomes active; the
   * rest stay inactive until the user switches).
   */
  addTab: (initial: {
    fileName: string;
    fileId?: string | null;
    fileHandle?: FileSystemFileHandle | null;
    /** Original filename with extension — see TabDoc.sourceFileName. */
    sourceFileName?: string | null;
    /** See TabDoc.derivedFrom. */
    derivedFrom?: VaultSaveSourceDto | null;
    snapshot?: PersistedSession | null;
    documentId?: string;
    activate?: boolean;
  }) => string;
  /**
   * Remove a tab. If the removed tab was active, the previous tab becomes
   * active (or null if no tabs remain). Returns the new active id (or null).
   *
   * Async: awaits the IDB session-record + stash deletion so a caller
   * that reloads the page immediately after close doesn't race the
   * pending delete (which would otherwise resurrect the "closed" tab
   * as a phantom on next mount). Callers that don't need the await
   * can wrap with `void` — the React state update is still synchronous.
   */
  closeTab: (id: string) => Promise<string | null>;
  /** Make `id` the active tab. No-op if it's already active. */
  setActiveTabId: (id: string) => void;
  /**
   * Write a snapshot to the named tab. Used during a switch to capture the
   * outgoing tab's state before the new one's snapshot is restored.
   */
  setTabSnapshot: (id: string, snapshot: PersistedSession | null) => void;
  /** Patch `fileName` / `fileId` / `fileHandle` / `sourceFileName` /
   *  `isDirty` on the active tab. `fileHandle` lets the Save-As path
   *  register a FileSystemFileHandle returned by `showSaveFilePicker`
   *  so a follow-up Save can write in-place instead of re-prompting.
   *  `sourceFileName` lets Save-As refresh the format hint when the
   *  user changes formats. */
  updateActiveTabMetadata: (patch: {
    fileName?: string;
    fileId?: string | null;
    fileHandle?: FileSystemFileHandle | null;
    sourceFileName?: string | null;
    derivedFrom?: VaultSaveSourceDto | null;
    isDirty?: boolean;
  }) => void;
}

export function useDocumentTabs(): UseDocumentTabsResult {
  const [tabs, setTabs] = useState<TabDoc[]>([]);
  const [activeTabId, setActiveTabIdState] = useState<string | null>(null);
  // Mirror state into refs so callbacks stay referentially stable but still
  // see fresh values.
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;
  const activeIdRef = useRef(activeTabId);
  activeIdRef.current = activeTabId;

  const addTab: UseDocumentTabsResult['addTab'] = useCallback((initial) => {
    const id = nextTabId();
    const tab: TabDoc = {
      id,
      // Stash documentId — reuse if caller provided (restore flow), else
      // mint a fresh UUID. Both halves of `id` and `documentId` matter:
      // `id` is React-only (regenerates each session); `documentId`
      // persists in IDB and survives reload.
      documentId: initial.documentId ?? crypto.randomUUID(),
      fileName: initial.fileName,
      fileId: initial.fileId ?? null,
      fileHandle: initial.fileHandle ?? null,
      sourceFileName: initial.sourceFileName ?? null,
      derivedFrom: initial.derivedFrom ?? null,
      isDirty: false,
      snapshot: initial.snapshot ?? null,
    };
    setTabs((prev) => [...prev, tab]);
    // Activate by default. Multi-tab restore passes `activate: false`
    // for tabs that should stay inactive after seeding.
    if (initial.activate !== false) {
      setActiveTabIdState(id);
    }
    return id;
  }, []);

  const closeTab: UseDocumentTabsResult['closeTab'] = useCallback(async (id) => {
    const prevTabs = tabsRef.current;
    const idx = prevTabs.findIndex((t) => t.id === id);
    if (idx === -1) return activeIdRef.current;
    const closing = prevTabs[idx];
    const remaining = prevTabs.filter((t) => t.id !== id);
    let nextActive: string | null = activeIdRef.current;
    if (activeIdRef.current === id) {
      // Pick the neighbor: prefer the tab to the left, else the one to the
      // right, else nothing.
      const neighborIdx = idx > 0 ? idx - 1 : 0;
      nextActive = remaining[neighborIdx]?.id ?? null;
      setActiveTabIdState(nextActive);
    }
    setTabs(remaining);
    // Tier 2 Group F — drop the closed tab's IDB session record AND any
    // associated PSD source-stash entry. Without this, IDB grows unboundedly
    // as the user opens / closes tabs, AND a closed tab's bytes would
    // resurface as a phantom tab on the next reload.
    //
    // Awaited (not fire-and-forget) so callers that reload the page
    // immediately after a close don't race the pending delete. The React
    // state updates above have already flushed synchronously, so the UI
    // updates without waiting for IDB.
    await clearSession(closing.documentId);
    return nextActive;
  }, []);

  const setActiveTabId: UseDocumentTabsResult['setActiveTabId'] = useCallback((id) => {
    if (activeIdRef.current === id) return;
    setActiveTabIdState(id);
  }, []);

  const setTabSnapshot: UseDocumentTabsResult['setTabSnapshot'] = useCallback((id, snapshot) => {
    setTabs((prev) => prev.map((t) => (t.id === id ? { ...t, snapshot } : t)));
  }, []);

  const updateActiveTabMetadata: UseDocumentTabsResult['updateActiveTabMetadata'] = useCallback(
    (patch) => {
      const id = activeIdRef.current;
      if (!id) return;
      setTabs((prev) => {
        let changed = false;
        const next = prev.map((t) => {
          if (t.id !== id) return t;
          // Skip if every field would be unchanged — keeps both the tab
          // object AND the tabs array referentially stable to avoid
          // triggering downstream re-renders for no-op metadata updates.
          if (
            (patch.fileName === undefined || patch.fileName === t.fileName) &&
            (patch.fileId === undefined || patch.fileId === t.fileId) &&
            (patch.fileHandle === undefined || patch.fileHandle === t.fileHandle) &&
            (patch.sourceFileName === undefined || patch.sourceFileName === t.sourceFileName) &&
            (patch.derivedFrom === undefined || patch.derivedFrom === t.derivedFrom) &&
            (patch.isDirty === undefined || patch.isDirty === t.isDirty)
          ) {
            return t;
          }
          changed = true;
          return {
            ...t,
            fileName: patch.fileName ?? t.fileName,
            fileId: patch.fileId === undefined ? t.fileId : patch.fileId,
            fileHandle: patch.fileHandle === undefined ? t.fileHandle : patch.fileHandle,
            sourceFileName:
              patch.sourceFileName === undefined ? t.sourceFileName : patch.sourceFileName,
            derivedFrom: patch.derivedFrom === undefined ? t.derivedFrom : patch.derivedFrom,
            isDirty: patch.isDirty ?? t.isDirty,
          };
        });
        return changed ? next : prev;
      });
    },
    [],
  );

  return {
    tabs,
    activeTabId,
    addTab,
    closeTab,
    setActiveTabId,
    setTabSnapshot,
    updateActiveTabMetadata,
  };
}
